import type { WorkspaceTabsPosition } from 'src/models/workspaceTabs';
import type { PrimaryColorName, SurfaceColorName } from 'src/theme/themeConfig';

import { defineStore } from 'pinia';
import { useAuthenticationStore } from 'src/stores/authentication';
import { useShortcutsStore } from 'src/stores/shortcuts';
import { applyAppFontSizePt, clampAppFontSizePt } from 'src/stores/userInterfaceSettings/appFontSize';
import { CreateANewColorSchemeCommand } from 'src/stores/userInterfaceSettings/commands/CreateANewColorSchemeCommand';
import { DeleteTheColorSchemeCommand } from 'src/stores/userInterfaceSettings/commands/DeleteTheColorSchemeCommand';
import { DeleteTheEditorStandardFontCommand } from 'src/stores/userInterfaceSettings/commands/DeleteTheEditorStandardFontCommand';
import { UpdateTheColorSchemeCommand } from 'src/stores/userInterfaceSettings/commands/UpdateTheColorSchemeCommand';
import { UpdateTheEditorStandardFontCommand } from 'src/stores/userInterfaceSettings/commands/UpdateTheEditorStandardFontCommand';
import { UpdateTheNotificationConfigurationCommand } from 'src/stores/userInterfaceSettings/commands/UpdateTheNotificationConfigurationCommand';
import { UpdateTheUserPreferenceCommand } from 'src/stores/userInterfaceSettings/commands/UpdateTheUserPreferenceCommand';
import {
	DEFAULT_DETAIL_TOOLBAR_BUTTON_COLORS,
	DetailToolbarButtonColorKey,
	isValidHexColor,
} from 'src/stores/userInterfaceSettings/detailToolbarButtonColors';
import {
	containsSubscription,
	materializeAllInstanceSubscriptions,
	parseIdoNotificationSubscriptions,
	serializeIdoNotificationSubscriptions,
} from 'src/stores/userInterfaceSettings/idoNotificationSubscriptions';
import {
	APP_FONT_SIZE_PERCENT_KEY,
	APP_FONT_SIZE_PT_KEY,
	APP_LANGUAGE_KEY,
	ARCHIVED_VISIBLE_IN_LISTS_KEY,
	AVATAR_MODE_IN_LIST_KEY,
	BOTTOM_LEFT_MAIN_TOOLBAR_SLOT_KEY,
	BOTTOM_RIGHT_MAIN_TOOLBAR_SLOT_KEY,
	CACHING_ENABLED,
	COMPACT_BUTTON_VIEW_KEY,
	CONTROLS_MODE_KEY,
	DATE_FORMAT_KEY,
	DEFAULT_LIST_PER_MODULE_KEY,
	DETAIL_TOOLBAR_BUTTON_COLORS_KEY,
	DOCUMENT_PDF_PREVIEW_ZOOM_KEY,
	EDITOR_STANDARD_FONT_FAMILY_KEY,
	EDITOR_STANDARD_FONT_SIZE_KEY,
	FINISHED_TRIGGERS_BACK_TO_LIST_KEY,
	HOVER_EFFECTS_KEY,
	KANBAN_REMEMBER_LAST_BOARD_KEY,
	LIST_GROUPING_MODE_KEY,
	MENU_BOTTOM_SLOT_SORT_INVERSE_KEY,
	MENU_ITEMS_SOLID_KEY,
	MENU_ORDER_KEY,
	NAVIGATION_MODE_KEY,
	NOTIFICATIONS_APPOINTMENT_ENABLED,
	NOTIFICATIONS_DOCUMENTS_ENABLED,
	NOTIFICATIONS_IDO_ENABLED,
	NOTIFICATIONS_MAIL_ENABLED,
	NOTIFICATIONS_MESSAGE_ENABLED,
	NOTIFICATIONS_NOTES_ENABLED,
	NOTIFICATIONS_PHONE_CALLS_ENABLED,
	NOTIFICATIONS_TASKS_ENABLED,
	SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY,
	SETUP_WIZARD_COMPLETED_KEY,
	SHARING_VISIBLE_IN_LISTS_KEY,
	SMOOTH_ANIMATIONS_KEY,
	SOFT_SHADOWS_KEY,
	SPLIT_VIEW_ENABLED,
	SPOTLIGHT_SEARCH_ENABLED,
	THEME_MODE_KEY,
	THEME_PRIMARY_COLOR_CUSTOM_KEY,
	THEME_PRIMARY_COLOR_KEY,
	THEME_SURFACE_COLOR_CUSTOM_KEY,
	THEME_SURFACE_COLOR_KEY,
	TIME_ZONE,
	TOP_LEFT_MAIN_TOOLBAR_SLOT_KEY,
	TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY,
	UNI_COLOR_BUTTONS_KEY,
	USER_ONLINE_LIST_VIEW_MODE,
	USER_ONLINE_POSITION_KEY,
	USER_ONLINE_SHOW_OFFLINE_USERS,
	WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW,
	WORKSPACE_TABS_ENABLED,
	WORKSPACE_TABS_POSITION,
	WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW,
} from 'src/stores/userInterfaceSettings/localStorageKeys';
import {
	GetTheColorSchemesQuery,
	GetTheEditorStandardFontQuery,
	GetTheNotificationConfigurationQuery,
	GetTheUserPreferencesQuery,
} from 'src/stores/userInterfaceSettings/queries';
import createDefaultUserInterfaceSettingsState, {
	type AppLanguage,
	resolveWorkspaceTabsPosition,
} from 'src/stores/userInterfaceSettings/state';
import { applyTheme } from 'src/theme/applyTheme';
import { resolvePrimaryColorName, resolveSurfaceColorName } from 'src/theme/themeConfig';
import { TimeZone } from 'timezones-list';

import {
	APP_FONT_SIZE_PT_PREFERENCE_KEY,
	COMPACT_BUTTON_VIEW_PREFERENCE_KEY,
	ConfigurableLayoutSlotButtons,
	DEFAULT_LIST_PER_MODULE_PREFERENCE_KEY,
	EditorStandardFont,
	EditorStandardFontRunType,
	IDO_NOTIFICATION_SUBSCRIPTIONS_PREFERENCE_KEY,
	IdoNotificationSubscription,
	KANBAN_REMEMBER_LAST_BOARD_PREFERENCE_KEY,
	MENU_ITEMS_SOLID_PREFERENCE_KEY,
	MenuOrder,
	NAVIGATION_MODE_PREFERENCE_KEY,
	NotificationConfiguration,
	NotificationConfigurationRunType,
	SETUP_WIZARD_COMPLETED_PREFERENCE_KEY,
	SHORTCUT_SETTINGS_PREFERENCE_KEY,
	SPOTLIGHT_SEARCH_ENABLED_PREFERENCE_KEY,
	SelectedColorSchemeForTheInstance,
	THEME_MODE_PREFERENCE_KEY,
	THEME_PRIMARY_COLOR_PREFERENCE_KEY,
	THEME_SURFACE_COLOR_PREFERENCE_KEY,
	TIMER_ENABLED_PREFERENCE_KEY,
	Theme,
	ThemeSchemeQuery,
	ThemeSchemeQueryRunType,
	UNI_COLOR_BUTTONS_PREFERENCE_KEY,
	USER_ONLINE_POSITION_PREFERENCE_KEY,
	USER_ONLINE_SHOW_OFFLINE_USERS_PREFERENCE_KEY,
	UserPreferences,
	UserPreferencesRunType,
	WIDGET_SIDEBAR_ACTIVE_WIDGETS_PREFERENCE_KEY,
	WIDGET_SIDEBAR_ENABLED_PREFERENCE_KEY,
	WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW_PREFERENCE_KEY,
	WORKSPACE_TABS_ENABLED_PREFERENCE_KEY,
	WORKSPACE_TABS_POSITION_PREFERENCE_KEY,
	WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW_PREFERENCE_KEY,
} from 'src/models/userInterfaceSettings';

import { ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY } from 'src/services/authentication';

import { CommandBus } from 'src/util/command/CommandBus';
import { setAppCssVar } from 'src/util/css';
import { type DateFormatKey } from 'src/util/date/dateFormatOptions';
import { clampPdfPreviewZoom } from 'src/util/pdf/pdfPreviewSizing';
import { canUseDesktopPointerInteractions } from 'src/util/platform/browserInteractionCapabilities';
import { applyDarkMode } from 'src/util/theme/applyDarkMode';

import {
	GetTheColorSchemesQueryCallBack,
	GetTheEditorStandardFontQueryCallBack,
	GetTheNotificationConfigurationQueryCallBack,
	GetTheUserPreferencesQueryCallBack,
} from './callbacks';
import {
	AddIdoNotificationSubscriptionPayload,
	ClearDefaultListForModulePayload,
	ClearEditorStandardFontPayload,
	ColorSchemeCommandPayload,
	DeleteColorSchemePayload,
	EditorStandardFontPayload,
	FetchColorSchemesPayload,
	FetchEditorStandardFontPayload,
	FetchNotificationConfigurationPayload,
	FetchUserPreferencesPayload,
	NavigationModePayload,
	NotificationTogglePayload,
	PreferenceTogglePayload,
	RemoveIdoNotificationSubscriptionGroupPayload,
	SetDefaultListForModulePayload,
	SetupWizardCommandPayload,
	SyncIdoNotificationSubscriptionsPayload,
} from './interfaces';
import { UserInterfaceSettingsStoreState } from './types';

const createDefaultState = (): UserInterfaceSettingsStoreState => createDefaultUserInterfaceSettingsState();

const toggleBodyClass = (className: string, enabled: boolean): void => {
	if (
		typeof document === 'undefined' ||
		document.body === undefined ||
		document.body === null ||
		document.body.classList === undefined
	) {
		return;
	}

	if (enabled) {
		document.body.classList.add(className);
		return;
	}

	document.body.classList.remove(className);
};

const applyThemeCssVars = (theme: Theme): void => {
	if (typeof Element === 'undefined') {
		return;
	}

	setAppCssVar('primary', theme.value.primary);
	setAppCssVar('secondary', theme.value.secondary);
	setAppCssVar('dark', theme.value.dark);
	setAppCssVar('dark-page', theme.value.dark);

	if (theme.value.uniColor) {
		setAppCssVar('uni-color', theme.value.uniColor);
		return;
	}

	setAppCssVar('uni-color', theme.value.primary);
};

export const useUserInterfaceSettingsStore = defineStore('userInterfaceSettings', {
	actions: {
		addIdoNotificationSubscription(payload: AddIdoNotificationSubscriptionPayload): void {
			let candidateSubscriptions: IdoNotificationSubscription[];

			if (payload.allInstances) {
				candidateSubscriptions = payload.accessibleInstanceIds.map(
					(instanceId): IdoNotificationSubscription => ({
						instanceId,
						savedByUserId: payload.savedByUserId,
						source: 'all',
					})
				);
			} else if (payload.instanceId === null) {
				candidateSubscriptions = [];
			} else {
				candidateSubscriptions = [
					{ instanceId: payload.instanceId, savedByUserId: payload.savedByUserId, source: 'explicit' },
				];
			}

			const newSubscriptions = candidateSubscriptions.filter(
				(candidate) => !containsSubscription(this.idoNotificationSubscriptions, candidate)
			);

			if (newSubscriptions.length === 0) {
				return;
			}

			this.idoNotificationSubscriptions = [...this.idoNotificationSubscriptions, ...newSubscriptions];
			this.privatePersistIdoNotificationSubscriptions(payload.commandBus);
		},
		clearDefaultListForModule(payload: ClearDefaultListForModulePayload): void {
			const next: Record<string, string> = { ...this.defaultListPerModule };
			delete next[payload.desktopApplicationItemId];
			this.defaultListPerModule = next;
			localStorage.setItem(DEFAULT_LIST_PER_MODULE_KEY, JSON.stringify(next));

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(DEFAULT_LIST_PER_MODULE_PREFERENCE_KEY, JSON.stringify(next))
			);
		},
		clearEditorStandardFont(payload: ClearEditorStandardFontPayload): void {
			localStorage.removeItem(EDITOR_STANDARD_FONT_FAMILY_KEY);
			localStorage.removeItem(EDITOR_STANDARD_FONT_SIZE_KEY);
			this.editorStandardFontFamily = '';
			this.editorStandardFontSize = '';

			void payload.commandBus?.sendCommand(new DeleteTheEditorStandardFontCommand());
		},
		createANewColorScheme(payload: ColorSchemeCommandPayload): void {
			void payload.commandBus
				?.sendCommand(new CreateANewColorSchemeCommand(payload.name, payload.scheme))
				?.then(() => {
					void this.fetchColorSchemes({
						queryStore: payload.queryStore,
					}).then(() => {
						const authenticationStore = useAuthenticationStore();
						this.setSelectedColorSchemes({
							instance: authenticationStore.activeInstanceAuthenticationConfigurationName,
							scheme: payload.name,
						});
					});
				});
		},
		deleteTheColorScheme(payload: DeleteColorSchemePayload): void {
			void payload.commandBus?.sendCommand(new DeleteTheColorSchemeCommand(payload.name))?.then(() => {
				void this.fetchColorSchemes({
					queryStore: payload.queryStore,
				}).then(() => {
					const authenticationStore = useAuthenticationStore();
					this.setSelectedColorSchemes({
						instance: authenticationStore.activeInstanceAuthenticationConfigurationName,
						scheme: 'Blue Da Ba Dee',
					});
				});
			});
		},
		ensureSelectedColorSchemeDefaults(): void {
			if (this.selectedColorSchemeForInstance.length > 0) {
				return;
			}

			this.selectedColorSchemeForInstance = [
				{
					instance: (localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY) as string) ?? '',
					scheme: 'Blue Da Ba Dee',
				},
			];
		},
		async fetchColorSchemes(payload: FetchColorSchemesPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<ThemeSchemeQuery[]>({
					callback: new GetTheColorSchemesQueryCallBack((themes) => this.setColorSchemes(themes)),
					createTime: new Date(),
					query: new GetTheColorSchemesQuery(),
					responseType: 'json',
					runType: ThemeSchemeQueryRunType,
				});
				return;
			}

			this.setColorSchemes([]);
		},
		async fetchEditorStandardFont(payload: FetchEditorStandardFontPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				return;
			}

			await payload.queryStore.query<EditorStandardFont[]>({
				callback: new GetTheEditorStandardFontQueryCallBack((font) =>
					this.privateApplyEditorStandardFont(font)
				),
				createTime: new Date(),
				query: new GetTheEditorStandardFontQuery(),
				responseType: 'json',
				runType: EditorStandardFontRunType,
			});
		},
		async fetchNotificationConfiguration(payload: FetchNotificationConfigurationPayload): Promise<void> {
			if (payload.queryStore !== undefined) {
				await payload.queryStore.query<NotificationConfiguration[]>({
					callback: new GetTheNotificationConfigurationQueryCallBack((notificationConfiguration) =>
						this.privateApplyNotificationConfiguration(notificationConfiguration)
					),
					createTime: new Date(),
					query: new GetTheNotificationConfigurationQuery(),
					responseType: 'json',
					runType: NotificationConfigurationRunType,
				});
			}
		},
		async fetchUserPreferences(payload: FetchUserPreferencesPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.userPreferencesLoaded = true;
				return;
			}

			try {
				await payload.queryStore.query<UserPreferences[]>({
					callback: new GetTheUserPreferencesQueryCallBack((preferences) =>
						this.privateApplyUserPreferences(preferences)
					),
					createTime: new Date(),
					query: new GetTheUserPreferencesQuery(),
					responseType: 'json',
					runType: UserPreferencesRunType,
				});
			} finally {
				this.userPreferencesLoaded = true;
			}
		},
		markSetupWizardCompleted(payload: SetupWizardCommandPayload): void {
			this.setupWizardCompleted = true;
			localStorage.setItem(SETUP_WIZARD_COMPLETED_KEY, 'true');

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(SETUP_WIZARD_COMPLETED_PREFERENCE_KEY, 'true')
			);
		},
		privateApplyEditorStandardFont(font: EditorStandardFont | undefined): void {
			if (font === undefined) {
				this.clearEditorStandardFont({ commandBus: undefined });
				return;
			}

			const fontFamily = font.fontFamily ?? '';
			const fontSize = font.fontSize ?? '';
			localStorage.setItem(EDITOR_STANDARD_FONT_FAMILY_KEY, fontFamily);
			localStorage.setItem(EDITOR_STANDARD_FONT_SIZE_KEY, fontSize);
			this.editorStandardFontFamily = fontFamily;
			this.editorStandardFontSize = fontSize;
		},
		privateApplyNotificationConfiguration(notificationConfiguration: NotificationConfiguration | undefined): void {
			this.notificationsTasksEnabled = notificationConfiguration?.notificationsTasksEnabled ?? false;
			this.notificationsPhoneCallsEnabled = notificationConfiguration?.notificationsPhoneCallsEnabled ?? false;
			this.notificationsMailEnabled = notificationConfiguration?.notificationsMailEnabled ?? false;
			this.notificationsAppointmentEnabled = notificationConfiguration?.notificationsAppointmentEnabled ?? false;
			this.notificationsMessageEnabled = notificationConfiguration?.notificationsMessageEnabled ?? false;
			this.notificationsNotesEnabled = notificationConfiguration?.notificationsNotesEnabled ?? false;
			this.notificationsDocumentsEnabled = notificationConfiguration?.notificationsDocumentsEnabled ?? false;
			this.notificationsIdoEnabled = notificationConfiguration?.notificationsIdoEnabled ?? false;
		},
		privateApplyUserPreferences(preferences: UserPreferences | undefined): void {
			if (preferences === undefined) {
				return;
			}

			this.idoNotificationSubscriptions = parseIdoNotificationSubscriptions(
				preferences[IDO_NOTIFICATION_SUBSCRIPTIONS_PREFERENCE_KEY]
			);

			const timerEnabledPreference = preferences[TIMER_ENABLED_PREFERENCE_KEY];

			if (timerEnabledPreference !== undefined) {
				this.timerEnabled = !/^false$/i.test(timerEnabledPreference);
			}

			const widgetSidebarEnabledPreference = preferences[WIDGET_SIDEBAR_ENABLED_PREFERENCE_KEY];

			if (widgetSidebarEnabledPreference !== undefined) {
				this.widgetSidebarEnabled = !/^false$/i.test(widgetSidebarEnabledPreference);
			}

			const navigationModePreference = preferences[NAVIGATION_MODE_PREFERENCE_KEY];

			if (
				navigationModePreference !== undefined &&
				(navigationModePreference === 'sidebar' || navigationModePreference === 'topbar')
			) {
				this.navigationMode = navigationModePreference;
				localStorage.setItem(NAVIGATION_MODE_KEY, navigationModePreference);
			}

			const widgetSidebarActiveWidgetsPreference = preferences[WIDGET_SIDEBAR_ACTIVE_WIDGETS_PREFERENCE_KEY];

			if (widgetSidebarActiveWidgetsPreference !== undefined) {
				try {
					const parsed = JSON.parse(widgetSidebarActiveWidgetsPreference) as string[];
					this.widgetSidebarActiveWidgets = Array.isArray(parsed) ? parsed.slice(0, 3) : [];
				} catch {
					// keep default
				}
			}

			const setupWizardCompletedPreference = preferences[SETUP_WIZARD_COMPLETED_PREFERENCE_KEY];
			if (setupWizardCompletedPreference !== undefined) {
				const isCompleted = /^true$/i.test(setupWizardCompletedPreference);
				this.setupWizardCompleted = isCompleted;
				localStorage.setItem(SETUP_WIZARD_COMPLETED_KEY, String(isCompleted));
			}

			const themeModePreference = preferences[THEME_MODE_PREFERENCE_KEY];
			if (themeModePreference === 'auto' || themeModePreference === 'dark' || themeModePreference === 'light') {
				this.themeMode = themeModePreference;
				localStorage.setItem(THEME_MODE_KEY, themeModePreference);
				applyDarkMode(themeModePreference);
			}

			const themePrimaryColorPreference = preferences[THEME_PRIMARY_COLOR_PREFERENCE_KEY];
			const themeSurfaceColorPreference = preferences[THEME_SURFACE_COLOR_PREFERENCE_KEY];
			let themeChanged = false;
			if (themePrimaryColorPreference !== undefined) {
				if (isValidHexColor(themePrimaryColorPreference)) {
					const hex = themePrimaryColorPreference.toLowerCase();
					this.themePrimaryColor = 'custom';
					this.themePrimaryColorCustom = hex;
					localStorage.setItem(THEME_PRIMARY_COLOR_KEY, 'custom');
					localStorage.setItem(THEME_PRIMARY_COLOR_CUSTOM_KEY, hex);
				} else {
					const resolved = resolvePrimaryColorName(themePrimaryColorPreference);
					this.themePrimaryColor = resolved;
					localStorage.setItem(THEME_PRIMARY_COLOR_KEY, resolved);
				}
				themeChanged = true;
			}
			if (themeSurfaceColorPreference !== undefined) {
				if (isValidHexColor(themeSurfaceColorPreference)) {
					const hex = themeSurfaceColorPreference.toLowerCase();
					this.themeSurfaceColor = 'custom';
					this.themeSurfaceColorCustom = hex;
					localStorage.setItem(THEME_SURFACE_COLOR_KEY, 'custom');
					localStorage.setItem(THEME_SURFACE_COLOR_CUSTOM_KEY, hex);
				} else {
					const resolved = resolveSurfaceColorName(themeSurfaceColorPreference);
					this.themeSurfaceColor = resolved;
					localStorage.setItem(THEME_SURFACE_COLOR_KEY, resolved);
				}
				themeChanged = true;
			}
			if (themeChanged) {
				applyTheme(
					this.themePrimaryColor,
					this.themeSurfaceColor,
					this.themePrimaryColorCustom,
					this.themeSurfaceColorCustom
				);
			}

			const menuItemsSolidPreference = preferences[MENU_ITEMS_SOLID_PREFERENCE_KEY];
			if (menuItemsSolidPreference !== undefined) {
				const value = /^true$/i.test(menuItemsSolidPreference);
				this.menuItemsSolid = value;
				localStorage.setItem(MENU_ITEMS_SOLID_KEY, JSON.stringify(value));
			}

			const kanbanRememberLastBoardPreference = preferences[KANBAN_REMEMBER_LAST_BOARD_PREFERENCE_KEY];
			if (kanbanRememberLastBoardPreference !== undefined) {
				const value = /^true$/i.test(kanbanRememberLastBoardPreference);
				this.kanbanRememberLastBoard = value;
				localStorage.setItem(KANBAN_REMEMBER_LAST_BOARD_KEY, String(value));
			}

			const appFontSizePtPreference = preferences[APP_FONT_SIZE_PT_PREFERENCE_KEY];
			if (appFontSizePtPreference !== undefined) {
				const parsed = Number(appFontSizePtPreference);
				if (Number.isFinite(parsed)) {
					const next = clampAppFontSizePt(parsed);
					this.appFontSizePt = next;
					localStorage.setItem(APP_FONT_SIZE_PT_KEY, String(next));
					applyAppFontSizePt(next);
				}
			}

			const compactButtonViewPreference = preferences[COMPACT_BUTTON_VIEW_PREFERENCE_KEY];
			if (compactButtonViewPreference !== undefined) {
				const value = /^true$/i.test(compactButtonViewPreference);
				this.shouldUseTheCompactButtonView = value;
				localStorage.setItem(COMPACT_BUTTON_VIEW_KEY, String(value));
			}

			const uniColorButtonsPreference = preferences[UNI_COLOR_BUTTONS_PREFERENCE_KEY];
			if (uniColorButtonsPreference !== undefined) {
				const value = /^true$/i.test(uniColorButtonsPreference);
				this.shouldUseUniColorButtons = value;
				localStorage.setItem(UNI_COLOR_BUTTONS_KEY, String(value));
			}

			const spotlightPreference = preferences[SPOTLIGHT_SEARCH_ENABLED_PREFERENCE_KEY];
			if (spotlightPreference !== undefined) {
				const value = /^true$/i.test(spotlightPreference);
				this.spotlightSearchEnabled = value;
				localStorage.setItem(SPOTLIGHT_SEARCH_ENABLED, String(value));
			}

			const userOnlinePositionPreference = preferences[USER_ONLINE_POSITION_PREFERENCE_KEY];
			if (
				userOnlinePositionPreference === 'left' ||
				userOnlinePositionPreference === 'right' ||
				userOnlinePositionPreference === 'off'
			) {
				this.userOnlinePosition = userOnlinePositionPreference;
				localStorage.setItem(USER_ONLINE_POSITION_KEY, userOnlinePositionPreference);
			}

			const userOnlineShowOfflinePreference = preferences[USER_ONLINE_SHOW_OFFLINE_USERS_PREFERENCE_KEY];
			if (userOnlineShowOfflinePreference !== undefined) {
				const value = /^true$/i.test(userOnlineShowOfflinePreference);
				this.userOnlineShowOfflineUsers = value;
				localStorage.setItem(USER_ONLINE_SHOW_OFFLINE_USERS, JSON.stringify(value));
			}

			const defaultListPerModulePreference = preferences[DEFAULT_LIST_PER_MODULE_PREFERENCE_KEY];
			if (defaultListPerModulePreference !== undefined) {
				try {
					const parsed: unknown = JSON.parse(defaultListPerModulePreference);
					if (parsed !== null && typeof parsed === 'object') {
						const sanitized: Record<string, string> = {};
						for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
							if (typeof value === 'string' && value.length > 0) {
								sanitized[key] = value;
							}
						}
						this.defaultListPerModule = sanitized;
						localStorage.setItem(DEFAULT_LIST_PER_MODULE_KEY, JSON.stringify(sanitized));
					}
				} catch {
					// keep local state — backend value was unparseable
				}
			}

			const shortcutSettingsPreference = preferences[SHORTCUT_SETTINGS_PREFERENCE_KEY];
			if (shortcutSettingsPreference !== undefined) {
				useShortcutsStore().applyShortcutSettingsPreference(shortcutSettingsPreference);
			}

			this.privateApplyWorkModePreferences(preferences);
		},
		privateApplyWorkModePreferences(preferences: UserPreferences): void {
			const applyBooleanPreference = (
				rawPreference: string | undefined,
				localStorageKey: string,
				assign: (value: boolean) => void
			): void => {
				if (rawPreference === undefined) {
					return;
				}

				const value = /^true$/i.test(rawPreference);
				assign(value);
				localStorage.setItem(localStorageKey, String(value));
			};

			const previousWorkspaceTabsEnabled = this.workspaceTabsEnabled;

			applyBooleanPreference(
				preferences[WORKSPACE_TABS_ENABLED_PREFERENCE_KEY],
				WORKSPACE_TABS_ENABLED,
				(value) => {
					this.workspaceTabsEnabled = value;
				}
			);

			applyBooleanPreference(
				preferences[WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW_PREFERENCE_KEY],
				WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW,
				(value) => {
					this.workspaceTabsCreateActionsInSplitView = value;
				}
			);

			applyBooleanPreference(
				preferences[WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW_PREFERENCE_KEY],
				WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW,
				(value) => {
					this.workspaceTabsRecordsInSplitView = value;
				}
			);

			const workspaceTabsPositionPreference = preferences[WORKSPACE_TABS_POSITION_PREFERENCE_KEY];
			if (workspaceTabsPositionPreference !== undefined) {
				const position = resolveWorkspaceTabsPosition(workspaceTabsPositionPreference);
				this.workspaceTabsPosition = position;
				localStorage.setItem(WORKSPACE_TABS_POSITION, position);
			}

			if (this.workspaceTabsEnabled === previousWorkspaceTabsEnabled) {
				return;
			}

			// Der Split-View wird bewusst nicht synchronisiert (er hängt am Zeigegerät, nicht am Konto).
			// Nach einem vom Server geänderten Arbeitsmodus muss die gerätelokale Ableitung aber neu
			// greifen, sonst bliebe das Tab-Layout auf einem Desktop stumm.
			this.restoreSplitViewFromPreference();
		},
		privatePersistIdoNotificationSubscriptions(commandBus: CommandBus | undefined): void {
			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					IDO_NOTIFICATION_SUBSCRIPTIONS_PREFERENCE_KEY,
					serializeIdoNotificationSubscriptions(this.idoNotificationSubscriptions)
				)
			);
		},
		privateSendNotificationConfigurationCommand(commandBus: CommandBus | undefined): void {
			void commandBus?.sendCommand(
				new UpdateTheNotificationConfigurationCommand(
					this.notificationsAppointmentEnabled,
					this.notificationsMailEnabled,
					this.notificationsMessageEnabled,
					this.notificationsPhoneCallsEnabled,
					this.notificationsTasksEnabled,
					this.notificationsNotesEnabled,
					this.notificationsDocumentsEnabled,
					this.notificationsIdoEnabled
				)
			);
		},
		removeIdoNotificationSubscriptionGroup(payload: RemoveIdoNotificationSubscriptionGroupPayload): void {
			const shouldRemove = (subscription: IdoNotificationSubscription): boolean => {
				if (payload.allInstances) {
					return subscription.source === 'all' && subscription.savedByUserId === payload.savedByUserId;
				}

				return (
					subscription.source === 'explicit' &&
					subscription.instanceId === payload.instanceId &&
					subscription.savedByUserId === payload.savedByUserId
				);
			};

			const remainingSubscriptions = this.idoNotificationSubscriptions.filter(
				(subscription) => !shouldRemove(subscription)
			);

			if (remainingSubscriptions.length === this.idoNotificationSubscriptions.length) {
				return;
			}

			this.idoNotificationSubscriptions = remainingSubscriptions;
			this.privatePersistIdoNotificationSubscriptions(payload.commandBus);
		},
		resetDetailToolbarButtonColors(): void {
			this.detailToolbarButtonColors = { ...DEFAULT_DETAIL_TOOLBAR_BUTTON_COLORS };
			localStorage.setItem(DETAIL_TOOLBAR_BUTTON_COLORS_KEY, JSON.stringify(this.detailToolbarButtonColors));
		},
		restoreSplitViewFromPreference(): void {
			if (this.workspaceTabsEnabled && canUseDesktopPointerInteractions()) {
				this.splitViewEnabled = true;
				return;
			}
			const storedPreference = localStorage.getItem(SPLIT_VIEW_ENABLED);
			this.splitViewEnabled = storedPreference !== null ? /true/i.test(storedPreference) : true;
		},
		setAppFontSizePt(appFontSizePt: number, commandBus?: CommandBus): void {
			const nextAppFontSizePt = clampAppFontSizePt(appFontSizePt);
			localStorage.setItem(APP_FONT_SIZE_PT_KEY, String(nextAppFontSizePt));
			localStorage.removeItem(APP_FONT_SIZE_PERCENT_KEY);
			this.appFontSizePt = nextAppFontSizePt;
			applyAppFontSizePt(nextAppFontSizePt);

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(APP_FONT_SIZE_PT_PREFERENCE_KEY, String(nextAppFontSizePt))
			);
		},
		setAppLanguage(appLanguage: AppLanguage): void {
			localStorage.setItem(APP_LANGUAGE_KEY, appLanguage);
			this.appLanguage = appLanguage;
		},
		setArchivedVisibleInLists(visible: boolean): void {
			localStorage.setItem(ARCHIVED_VISIBLE_IN_LISTS_KEY, String(visible));
			this.archivedVisibleInLists = visible;
		},
		setAvatarModeInList(avatarMode: 'avatar' | 'avatarAndModule' | 'module'): void {
			localStorage.setItem(AVATAR_MODE_IN_LIST_KEY, avatarMode);
			this.avatarModeInList = avatarMode;
		},
		setBottomLeftMainToolbarSlot(configurableLayoutSlotButton: ConfigurableLayoutSlotButtons): void {
			const nextSlotButton =
				configurableLayoutSlotButton === ConfigurableLayoutSlotButtons.CONTACT_INFORMATION
					? ConfigurableLayoutSlotButtons.EMTPY_SLOT
					: configurableLayoutSlotButton;
			localStorage.setItem(BOTTOM_LEFT_MAIN_TOOLBAR_SLOT_KEY, JSON.stringify(nextSlotButton));
			this.bottomLeftMainToolbarSlot = nextSlotButton;
		},
		setBottomRightMainToolbarSlot(configurableLayoutSlotButton: ConfigurableLayoutSlotButtons): void {
			const nextSlotButton =
				configurableLayoutSlotButton === ConfigurableLayoutSlotButtons.CONTACT_INFORMATION
					? ConfigurableLayoutSlotButtons.EMTPY_SLOT
					: configurableLayoutSlotButton;
			localStorage.setItem(BOTTOM_RIGHT_MAIN_TOOLBAR_SLOT_KEY, JSON.stringify(nextSlotButton));
			this.bottomRightMainToolbarSlot = nextSlotButton;
		},
		setColorSchemes(colorSchemes: Array<Theme>): void {
			const colorSchemesChecked: Array<Theme> = [];
			colorSchemes.forEach((colorSchemeToCheck) => {
				colorSchemesChecked.push(colorSchemeToCheck);
			});

			this.colorSchemes = colorSchemesChecked;
		},
		setCompactButtonView(shouldUseTheCompactButtonView: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(COMPACT_BUTTON_VIEW_KEY, String(shouldUseTheCompactButtonView));
			this.shouldUseTheCompactButtonView = shouldUseTheCompactButtonView;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					COMPACT_BUTTON_VIEW_PREFERENCE_KEY,
					String(shouldUseTheCompactButtonView)
				)
			);
		},
		setControlsMode(controlsMode: 'contextMenu' | 'swipeMenu'): void {
			localStorage.setItem(CONTROLS_MODE_KEY, controlsMode);
			this.controlsMode = controlsMode;
		},
		setDateFormat(dateFormat: DateFormatKey): void {
			localStorage.setItem(DATE_FORMAT_KEY, dateFormat);
			this.dateFormat = dateFormat;
		},
		setDefaultListForModule(payload: SetDefaultListForModulePayload): void {
			const next: Record<string, string> = {
				...this.defaultListPerModule,
				[payload.desktopApplicationItemId]: payload.serializedMemento,
			};
			this.defaultListPerModule = next;
			localStorage.setItem(DEFAULT_LIST_PER_MODULE_KEY, JSON.stringify(next));

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(DEFAULT_LIST_PER_MODULE_PREFERENCE_KEY, JSON.stringify(next))
			);
		},
		setDetailToolbarButtonColor(action: DetailToolbarButtonColorKey, hex: string): void {
			if (!isValidHexColor(hex)) {
				return;
			}

			this.detailToolbarButtonColors = {
				...this.detailToolbarButtonColors,
				[action]: hex,
			};
			localStorage.setItem(DETAIL_TOOLBAR_BUTTON_COLORS_KEY, JSON.stringify(this.detailToolbarButtonColors));
		},
		setDocumentPdfPreviewZoom(documentPdfPreviewZoom: number): void {
			const clampedZoom = clampPdfPreviewZoom(documentPdfPreviewZoom);
			localStorage.setItem(DOCUMENT_PDF_PREVIEW_ZOOM_KEY, String(clampedZoom));
			this.documentPdfPreviewZoom = clampedZoom;
		},
		setEditorStandardFont(payload: EditorStandardFontPayload): void {
			localStorage.setItem(EDITOR_STANDARD_FONT_FAMILY_KEY, payload.fontFamily);
			localStorage.setItem(EDITOR_STANDARD_FONT_SIZE_KEY, payload.fontSize);
			this.editorStandardFontFamily = payload.fontFamily;
			this.editorStandardFontSize = payload.fontSize;

			void payload.commandBus?.sendCommand(
				new UpdateTheEditorStandardFontCommand(
					payload.fontSize,
					payload.fontSizeView,
					payload.fontFamily,
					payload.fontFamilyView
				)
			);
		},
		setFinishedTriggersBackToList(finishedTriggersBackToList: boolean): void {
			localStorage.setItem(FINISHED_TRIGGERS_BACK_TO_LIST_KEY, String(finishedTriggersBackToList));
			this.finishedTriggersBackToList = finishedTriggersBackToList;
		},
		setKanbanRememberLastBoard(kanbanRememberLastBoard: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(KANBAN_REMEMBER_LAST_BOARD_KEY, String(kanbanRememberLastBoard));
			this.kanbanRememberLastBoard = kanbanRememberLastBoard;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					KANBAN_REMEMBER_LAST_BOARD_PREFERENCE_KEY,
					String(kanbanRememberLastBoard)
				)
			);
		},
		setListGroupingMode(listGroupingMode: 'moduleGrouping' | 'none' | 'timeFrameGrouping'): void {
			localStorage.setItem(LIST_GROUPING_MODE_KEY, listGroupingMode);
			this.listGroupingMode = listGroupingMode;
		},
		setMenuBottomSlotSortInverse(menuBottomSlotSortInverse: boolean): void {
			localStorage.setItem(MENU_BOTTOM_SLOT_SORT_INVERSE_KEY, JSON.stringify(menuBottomSlotSortInverse));
			this.menuBottomSlotSortInverse = menuBottomSlotSortInverse;
		},
		setMenuItemsSolid(menuItemsSolid: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(MENU_ITEMS_SOLID_KEY, JSON.stringify(menuItemsSolid));
			this.menuItemsSolid = menuItemsSolid;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(MENU_ITEMS_SOLID_PREFERENCE_KEY, String(menuItemsSolid))
			);
		},
		setMenuOrder(menuOrder: MenuOrder): void {
			localStorage.setItem(MENU_ORDER_KEY, JSON.stringify(menuOrder));
			this.menuOrder = menuOrder;
		},
		setNavigationMode(payload: NavigationModePayload): void {
			localStorage.setItem(NAVIGATION_MODE_KEY, payload.value);
			this.navigationMode = payload.value;

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(NAVIGATION_MODE_PREFERENCE_KEY, payload.value)
			);
		},
		setNotificationsAppointmentEnabled(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_APPOINTMENT_ENABLED, String(payload.value));
			this.notificationsAppointmentEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsDocumentsEnable(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_DOCUMENTS_ENABLED, String(payload.value));
			this.notificationsDocumentsEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsIdoEnabled(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_IDO_ENABLED, String(payload.value));
			this.notificationsIdoEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsMailEnabled(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_MAIL_ENABLED, String(payload.value));
			this.notificationsMailEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsMessageEnabled(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_MESSAGE_ENABLED, String(payload.value));
			this.notificationsMessageEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsNotesEnable(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_NOTES_ENABLED, String(payload.value));
			this.notificationsNotesEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsPhoneCallsEnabled(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_PHONE_CALLS_ENABLED, String(payload.value));
			this.notificationsPhoneCallsEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setNotificationsTasksEnable(payload: NotificationTogglePayload): void {
			localStorage.setItem(NOTIFICATIONS_TASKS_ENABLED, String(payload.value));
			this.notificationsTasksEnabled = payload.value;
			this.privateSendNotificationConfigurationCommand(payload.commandBus);
		},
		setPerformanceCachingEnabled(performanceCachingEnabled: boolean): void {
			localStorage.setItem(CACHING_ENABLED, String(performanceCachingEnabled));
			this.performanceCachingEnabled = performanceCachingEnabled;
		},
		setSelectedColorSchemes(selectedColorSchemeForTheInstance: SelectedColorSchemeForTheInstance): void {
			let currentSelectedColorSchemes = JSON.parse(
				localStorage.getItem(SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY) as string
			) as Array<SelectedColorSchemeForTheInstance> | null;

			if (currentSelectedColorSchemes === null) {
				currentSelectedColorSchemes = [];
			}

			currentSelectedColorSchemes = currentSelectedColorSchemes.filter(
				(storedSelectedColorSchemeForTheInstance) =>
					storedSelectedColorSchemeForTheInstance.instance !== selectedColorSchemeForTheInstance.instance
			);
			currentSelectedColorSchemes.push(selectedColorSchemeForTheInstance);

			const colorScheme = this.colorSchemes.find((candidateColorScheme) => {
				return candidateColorScheme.label === selectedColorSchemeForTheInstance.scheme;
			});
			if (colorScheme !== undefined) {
				applyThemeCssVars(colorScheme);
			}

			localStorage.setItem(
				SELECTED_COLOR_SCHEME_FOR_THE_INSTANCE_KEY,
				JSON.stringify(currentSelectedColorSchemes)
			);
			this.selectedColorSchemeForInstance = currentSelectedColorSchemes;
		},
		setSharingVisibleInLists(visible: boolean): void {
			localStorage.setItem(SHARING_VISIBLE_IN_LISTS_KEY, String(visible));
			this.sharingVisibleInLists = visible;
		},
		setShouldUseHoverEffects(shouldUseHoverEffects: boolean): void {
			localStorage.setItem(HOVER_EFFECTS_KEY, String(shouldUseHoverEffects));
			toggleBodyClass('hover-effects', shouldUseHoverEffects);
		},
		setShouldUseSmoothAnimations(shouldUseSmoothAnimations: boolean): void {
			localStorage.setItem(SMOOTH_ANIMATIONS_KEY, String(shouldUseSmoothAnimations));
			toggleBodyClass('smooth-animations', shouldUseSmoothAnimations);
		},
		setShouldUseSoftShadows(shouldUseSoftShadows: boolean): void {
			localStorage.setItem(SOFT_SHADOWS_KEY, String(shouldUseSoftShadows));
			this.shouldUseSoftShadows = shouldUseSoftShadows;
			toggleBodyClass('soft-shadows', shouldUseSoftShadows);
		},
		setShouldUseUniColorButtons(shouldUseUniColorButtons: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(UNI_COLOR_BUTTONS_KEY, String(shouldUseUniColorButtons));
			this.shouldUseUniColorButtons = shouldUseUniColorButtons;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(UNI_COLOR_BUTTONS_PREFERENCE_KEY, String(shouldUseUniColorButtons))
			);
		},
		setSplitViewEnabled(splitViewEnabled: boolean): void {
			localStorage.setItem(SPLIT_VIEW_ENABLED, String(splitViewEnabled));
			this.splitViewEnabled = splitViewEnabled;
		},
		setSpotlightSearchEnabled(spotlightSearchEnabled: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(SPOTLIGHT_SEARCH_ENABLED, String(spotlightSearchEnabled));
			this.spotlightSearchEnabled = spotlightSearchEnabled;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					SPOTLIGHT_SEARCH_ENABLED_PREFERENCE_KEY,
					String(spotlightSearchEnabled)
				)
			);
		},
		setThemeMode(themeMode: 'auto' | 'dark' | 'light', commandBus?: CommandBus): void {
			localStorage.setItem(THEME_MODE_KEY, themeMode);
			this.themeMode = themeMode;

			void commandBus?.sendCommand(new UpdateTheUserPreferenceCommand(THEME_MODE_PREFERENCE_KEY, themeMode));
		},
		setThemePrimaryColor(color: PrimaryColorName, commandBus?: CommandBus): void {
			localStorage.setItem(THEME_PRIMARY_COLOR_KEY, color);
			this.themePrimaryColor = color;
			applyTheme(color, this.themeSurfaceColor, this.themePrimaryColorCustom, this.themeSurfaceColorCustom);

			void commandBus?.sendCommand(new UpdateTheUserPreferenceCommand(THEME_PRIMARY_COLOR_PREFERENCE_KEY, color));
		},
		setThemePrimaryColorCustom(hex: string, commandBus?: CommandBus): void {
			if (!isValidHexColor(hex)) {
				return;
			}

			const normalized = hex.toLowerCase();
			localStorage.setItem(THEME_PRIMARY_COLOR_CUSTOM_KEY, normalized);
			this.themePrimaryColorCustom = normalized;

			if (this.themePrimaryColor !== 'custom') {
				localStorage.setItem(THEME_PRIMARY_COLOR_KEY, 'custom');
				this.themePrimaryColor = 'custom';
			}

			applyTheme(this.themePrimaryColor, this.themeSurfaceColor, normalized, this.themeSurfaceColorCustom);

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(THEME_PRIMARY_COLOR_PREFERENCE_KEY, normalized)
			);
		},
		setThemeSurfaceColor(surface: SurfaceColorName, commandBus?: CommandBus): void {
			localStorage.setItem(THEME_SURFACE_COLOR_KEY, surface);
			this.themeSurfaceColor = surface;
			applyTheme(this.themePrimaryColor, surface, this.themePrimaryColorCustom, this.themeSurfaceColorCustom);

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(THEME_SURFACE_COLOR_PREFERENCE_KEY, surface)
			);
		},
		setThemeSurfaceColorCustom(hex: string, commandBus?: CommandBus): void {
			if (!isValidHexColor(hex)) {
				return;
			}

			const normalized = hex.toLowerCase();
			localStorage.setItem(THEME_SURFACE_COLOR_CUSTOM_KEY, normalized);
			this.themeSurfaceColorCustom = normalized;

			if (this.themeSurfaceColor !== 'custom') {
				localStorage.setItem(THEME_SURFACE_COLOR_KEY, 'custom');
				this.themeSurfaceColor = 'custom';
			}

			applyTheme(this.themePrimaryColor, this.themeSurfaceColor, this.themePrimaryColorCustom, normalized);

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(THEME_SURFACE_COLOR_PREFERENCE_KEY, normalized)
			);
		},
		setTimerEnabled(payload: PreferenceTogglePayload): void {
			this.timerEnabled = payload.value;

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(TIMER_ENABLED_PREFERENCE_KEY, String(payload.value))
			);
		},
		setTimezone(timeZone: 'auto' | TimeZone): void {
			localStorage.setItem(TIME_ZONE, JSON.stringify(timeZone));
			this.timeZone = timeZone;
		},
		setTopLeftMainToolbarSlot(configurableLayoutSlotButtons: Array<ConfigurableLayoutSlotButtons>): void {
			const nextSlotButtons = configurableLayoutSlotButtons.map((configurableLayoutSlotButton) =>
				configurableLayoutSlotButton === ConfigurableLayoutSlotButtons.CONTACT_INFORMATION
					? ConfigurableLayoutSlotButtons.EMTPY_SLOT
					: configurableLayoutSlotButton
			);
			localStorage.setItem(TOP_LEFT_MAIN_TOOLBAR_SLOT_KEY, JSON.stringify(nextSlotButtons));
			this.topLeftMainToolbarSlot = nextSlotButtons;
		},
		setTopRightMainToolbarSlot(configurableLayoutSlotButtons: Array<ConfigurableLayoutSlotButtons>): void {
			const nextSlotButtons = configurableLayoutSlotButtons.map((configurableLayoutSlotButton) =>
				configurableLayoutSlotButton === ConfigurableLayoutSlotButtons.CONTACT_INFORMATION
					? ConfigurableLayoutSlotButtons.EMTPY_SLOT
					: configurableLayoutSlotButton
			);
			localStorage.setItem(TOP_RIGHT_MAIN_TOOLBAR_SLOT_KEY, JSON.stringify(nextSlotButtons));
			this.topRightMainToolbarSlot = nextSlotButtons;
		},
		setUserOnlineListViewMode(userOnlineListViewMode: 'compressed' | 'large'): void {
			localStorage.setItem(USER_ONLINE_LIST_VIEW_MODE, userOnlineListViewMode);
			this.userOnlineListViewMode = userOnlineListViewMode;
		},
		setUserOnlinePosition(position: 'left' | 'off' | 'right', commandBus?: CommandBus): void {
			localStorage.setItem(USER_ONLINE_POSITION_KEY, position);
			this.userOnlinePosition = position;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(USER_ONLINE_POSITION_PREFERENCE_KEY, position)
			);
		},
		setUserOnlineShowOfflineUsers(userOnlineShowOfflineUsers: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(USER_ONLINE_SHOW_OFFLINE_USERS, JSON.stringify(userOnlineShowOfflineUsers));
			this.userOnlineShowOfflineUsers = userOnlineShowOfflineUsers;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					USER_ONLINE_SHOW_OFFLINE_USERS_PREFERENCE_KEY,
					String(userOnlineShowOfflineUsers)
				)
			);
		},
		setWidgetSidebarActiveWidgets(payload: { commandBus: CommandBus | undefined; widgets: string[] }): void {
			this.widgetSidebarActiveWidgets = payload.widgets.slice(0, 3);

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					WIDGET_SIDEBAR_ACTIVE_WIDGETS_PREFERENCE_KEY,
					JSON.stringify(this.widgetSidebarActiveWidgets)
				)
			);
		},
		setWidgetSidebarEnabled(payload: PreferenceTogglePayload): void {
			this.widgetSidebarEnabled = payload.value;

			void payload.commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(WIDGET_SIDEBAR_ENABLED_PREFERENCE_KEY, String(payload.value))
			);
		},
		setWorkspaceTabsCreateActionsInSplitView(
			workspaceTabsCreateActionsInSplitView: boolean,
			commandBus?: CommandBus
		): void {
			localStorage.setItem(
				WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW,
				String(workspaceTabsCreateActionsInSplitView)
			);
			this.workspaceTabsCreateActionsInSplitView = workspaceTabsCreateActionsInSplitView;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					WORKSPACE_TABS_CREATE_ACTIONS_IN_SPLIT_VIEW_PREFERENCE_KEY,
					String(workspaceTabsCreateActionsInSplitView)
				)
			);
		},
		setWorkspaceTabsEnabled(workspaceTabsEnabled: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(WORKSPACE_TABS_ENABLED, String(workspaceTabsEnabled));
			this.workspaceTabsEnabled = workspaceTabsEnabled;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(WORKSPACE_TABS_ENABLED_PREFERENCE_KEY, String(workspaceTabsEnabled))
			);
		},
		setWorkspaceTabsPosition(workspaceTabsPosition: WorkspaceTabsPosition, commandBus?: CommandBus): void {
			localStorage.setItem(WORKSPACE_TABS_POSITION, workspaceTabsPosition);
			this.workspaceTabsPosition = workspaceTabsPosition;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(WORKSPACE_TABS_POSITION_PREFERENCE_KEY, workspaceTabsPosition)
			);
		},
		setWorkspaceTabsRecordsInSplitView(workspaceTabsRecordsInSplitView: boolean, commandBus?: CommandBus): void {
			localStorage.setItem(WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW, String(workspaceTabsRecordsInSplitView));
			this.workspaceTabsRecordsInSplitView = workspaceTabsRecordsInSplitView;

			void commandBus?.sendCommand(
				new UpdateTheUserPreferenceCommand(
					WORKSPACE_TABS_RECORDS_IN_SPLIT_VIEW_PREFERENCE_KEY,
					String(workspaceTabsRecordsInSplitView)
				)
			);
		},
		suppressSplitViewForNarrowViewport(): void {
			this.splitViewEnabled = false;
		},
		syncIdoNotificationSubscriptions(payload: SyncIdoNotificationSubscriptionsPayload): void {
			if (payload.accessibleInstanceIds.length === 0) {
				return;
			}

			const rematerializedSubscriptions = materializeAllInstanceSubscriptions(
				this.idoNotificationSubscriptions,
				payload.accessibleInstanceIds
			);

			const isUnchanged =
				rematerializedSubscriptions.length === this.idoNotificationSubscriptions.length &&
				rematerializedSubscriptions.every((subscription) =>
					containsSubscription(this.idoNotificationSubscriptions, subscription)
				);

			if (isUnchanged) {
				return;
			}

			this.idoNotificationSubscriptions = rematerializedSubscriptions;
			this.privatePersistIdoNotificationSubscriptions(payload.commandBus);
		},
		updateTheColorScheme(payload: ColorSchemeCommandPayload): void {
			void payload.commandBus
				?.sendCommand(new UpdateTheColorSchemeCommand(payload.name, payload.scheme))
				?.then(() => {
					void this.fetchColorSchemes({
						queryStore: payload.queryStore,
					}).then(() => {
						const authenticationStore = useAuthenticationStore();
						this.setSelectedColorSchemes({
							instance: authenticationStore.activeInstanceAuthenticationConfigurationName,
							scheme: payload.name,
						});
					});
				});
		},
	},
	getters: {
		activeTheme(state): Theme {
			const fallbackTheme: Theme = {
				deletable: false,
				label: 'Blue Da Ba Dee',
				value: {
					dark: '#252525',
					neon: false,
					primary: '#004994',
					secondary: '#023366',
					uniColor: '#004994',
				},
			};

			const authenticationStore = useAuthenticationStore();
			const selectedColorSchemeForInstance = state.selectedColorSchemeForInstance.find(
				(colorScheme) =>
					colorScheme.instance === authenticationStore.activeInstanceAuthenticationConfigurationName
			);

			if (selectedColorSchemeForInstance !== undefined) {
				return (
					state.colorSchemes.find(
						(colorScheme) => colorScheme.label === selectedColorSchemeForInstance.scheme
					) ?? fallbackTheme
				);
			}

			return fallbackTheme;
		},
		compactButtonView(state): boolean {
			return state.shouldUseTheCompactButtonView;
		},
		getDefaultListForModule:
			(state) =>
			(desktopApplicationItemId: string): null | string => {
				const stored = state.defaultListPerModule[desktopApplicationItemId];
				return stored !== undefined && stored.length > 0 ? stored : null;
			},
		selectedColorSchemes(state): Array<SelectedColorSchemeForTheInstance> {
			return state.selectedColorSchemeForInstance;
		},
		timezone(state): 'auto' | TimeZone {
			return state.timeZone;
		},
		userOnlineClassicBelowMenu(state): boolean {
			return state.userOnlinePosition === 'left';
		},
		userOnlineInRightDrawer(state): boolean {
			return state.userOnlinePosition === 'right';
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
